import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Trash2, ChevronLeft, ChevronRight, Upload, Check, X, AlertTriangle,
  FileSpreadsheet, FileText, Loader2, CloudUpload, Clock, CheckCircle2,
  Rows3, Columns3, Coins, Sparkles, ListChecks,
} from "lucide-react";
import { showToast } from "toast/toastHelper";
import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import UserSidebar from "./components/UserSidebar";
import Header from "units/dashboard/components/Header";
import { uploadFile } from "./services/knowlageBaseInfo";
import {
  getStructuredDataList,
  getStructuredDataProcess,
  confirmStructuredData,
  deleteStructuredData,
} from "./services/chankinfo";
import moment from "moment-jalaali";
import { useTranslation } from "react-i18next";

const ALLOWED_EXT = [".xlsx", ".csv"];
const ITEMS_PER_PAGE = 4;

/* ================================================================
 *  Helpers
 * ================================================================ */
function getExt(filename) {
  return "." + filename.split(".").pop().toLowerCase();
}

/* ================================================================
 *  Small shared UI
 * ================================================================ */
const Spinner = ({ tone = "white", size = 16 }) => {
  const colorClass =
    tone === "white"
      ? "border-white border-t-transparent"
      : tone === "teal"
        ? "border-teal-500 border-t-transparent"
        : tone === "amber"
          ? "border-amber-500 border-t-transparent"
          : "border-rose-500 border-t-transparent";
  return (
    <span
      className={`inline-block rounded-full border-2 animate-spin ${colorClass}`}
      style={{ width: size, height: size }}
    />
  );
};

/* ================================================================
 *  Main Component
 * ================================================================ */
function FileUploadManagerInner() {
  const { t } = useTranslation(["user"]);

  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const accessToken = useSelector((state) => state.auth.accessToken);

  const [pending, setPending] = useState(null);
  const [confirmedFiles, setConfirmedFiles] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingConfirmingId, setPendingConfirmingId] = useState(null);
  const [pendingDeletingId, setPendingDeletingId] = useState(null);

  const fileInputRef = useRef(null);

  /* ---------- formatSize داخل کامپوننت تا به t دسترسی داشته باشه ---------- */
  const formatSize = useCallback((bytes) => {
    if (bytes === null || bytes === undefined) return null;
    if (bytes < 1024) return `${bytes} ${t("fileUploadManager.sizes.byte")}`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ${t("fileUploadManager.sizes.kilobyte")}`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} ${t("fileUploadManager.sizes.megabyte")}`;
  }, [t]);

  const totalPages = Math.max(1, Math.ceil(confirmedFiles.length / ITEMS_PER_PAGE));

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return confirmedFiles.slice(start, start + ITEMS_PER_PAGE);
  }, [confirmedFiles, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  /* ---------- بستن سایدبار موبایل هنگام resize به دسکتاپ ---------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) toggleSidebar();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, toggleSidebar]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await getStructuredDataList(accessToken);
      if (res?.ok) {
        const confirmedData = res.data?.confirmed || [];
        const pendingData = res.data?.pending || [];

        const mappedConfirmed = confirmedData.map((f) => ({
          id: f.id,
          name: f.name,
          url: f.url,
          size: f.sizeBytes ?? null,
          rows: f.rowCount ?? null,
          cols: f.colCount ?? null,
          computable: true,
          createdAt: f.createdAt,
          updatedAt: f.updatedAt,
          estimatedTokens: f.estimatedTokens ?? null,
          sufficientTokens: f.sufficientTokens ?? null,
        }));

        const mappedPending = pendingData.map((f) => ({
          id: f.id,
          name: f.name,
          url: f.url,
          size: f.sizeBytes ?? null,
          rows: f.rowCount ?? null,
          cols: f.colCount ?? null,
          computable: true,
          createdAt: f.createdAt,
          updatedAt: f.updatedAt,
          estimatedTokens: f.estimatedTokens ?? null,
          sufficientTokens: f.sufficientTokens ?? null,
        }));
        if (res.data.warning) showToast(res.data.warning, "warning");
        setConfirmedFiles(mappedConfirmed);
        setPendingFiles(mappedPending);
      }
    } catch (error) {
      console.error("getStructuredDataList error:", error);
    } finally {
      setListLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken) fetchList();
  }, [accessToken, fetchList]);

  useEffect(() => {
    setCurrentPage((page) =>
      Math.min(page, Math.max(1, Math.ceil(confirmedFiles.length / ITEMS_PER_PAGE)))
    );
  }, [confirmedFiles]);

  /* ---------- Upload handlers ---------- */
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = getExt(file.name);

    if (!ALLOWED_EXT.includes(ext)) {
      setPending({
        file, name: file.name, size: file.size, ext,
        error: t("fileUploadManager.errors.formatNotSupported"),
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setPending({ file, name: file.name, size: file.size, ext, loading: true });

    try {
      const uploadRes = await uploadFile({ file, accessToken });
      if (!uploadRes?.ok) {
        setPending(null);
        return;
      }

      if (uploadRes.data?.message) showToast(uploadRes.data.message, "info");

      const fileId = uploadRes.data?.id || uploadRes.data?._id;
      if (!fileId) {
        showToast(t("fileUploadManager.errors.fileIdMissing"), "error");
        setPending(null);
        return;
      }

      const processRes = await getStructuredDataProcess({ accessToken, fileId });
      if (!processRes?.ok) {
        setPending(null);
        return;
      }

      if (processRes.data?.sufficientTokens === false) {
        showToast(t("fileUploadManager.insufficientTokens"), "error");
      }

      setPending({
        file, fileId, name: file.name,
        size: processRes.data?.sizeBytes ?? file.size,
        ext, loading: false,
        rows: processRes.data?.rowCount ?? null,
        cols: processRes.data?.colCount ?? null,
        computable: true,
        sufficientTokens: processRes.data?.sufficientTokens,
        estimatedTokens: processRes.data?.estimatedTokens,
      });

      await fetchList();
    } catch (error) {
      console.error("upload/process error:", error);
      showToast(t("fileUploadManager.errors.uploadFailed"), "error");
      setPending(null);
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileChange({ target: { files: [file] } });
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };

  const confirmAdd = async () => {
    if (!pending || pending.error || !pending.fileId || submitting) return;
    if (pending.sufficientTokens === false) {
      showToast(t("fileUploadManager.notEnoughTokens"), "error");
      return;
    }
    setSubmitting(true);
    try {
      const confirmRes = await confirmStructuredData({ accessToken, fileId: pending.fileId });
      if (confirmRes.data?.warning) showToast(confirmRes.data.warning, "warning");
      if (!confirmRes?.ok) return;
      showToast(t("fileUploadManager.toasts.uploadSuccess"), "success");
      setPending(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchList();
    } finally {
      setSubmitting(false);
    }
  };

  const cancelAdd = () => {
    setPending(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const confirmPendingFile = async (file) => {
    if (!file?.id || pendingConfirmingId) return;
    if (file.sufficientTokens === false) {
      showToast(t("fileUploadManager.notEnoughTokens"), "error");
      return;
    }
    setPendingConfirmingId(file.id);
    try {
      const res = await confirmStructuredData({ accessToken, fileId: file.id });
      if (!res?.ok) return;
      showToast(t("fileUploadManager.toasts.confirmSuccess"), "success");
      await fetchList();
    } finally {
      setPendingConfirmingId(null);
    }
  };

  const deletePendingFile = async (file) => {
    if (!file?.id || pendingDeletingId) return;
    setPendingDeletingId(file.id);
    try {
      const res = await deleteStructuredData({ accessToken, fileId: file.id });
      if (!res?.ok) return;
      showToast(t("fileUploadManager.toasts.deleteSuccess"), "success");
      await fetchList();
    } finally {
      setPendingDeletingId(null);
    }
  };

  const requestDelete = (file) => setDeleteTarget(file);
  const cancelDelete = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      const res = await deleteStructuredData({ accessToken, fileId: deleteTarget.id });
      if (!res?.ok) return;
      showToast(t("fileUploadManager.toasts.deleteSuccess"), "success");
      setDeleteTarget(null);
      await fetchList();
    } finally {
      setDeletingId(null);
    }
  };

  /* ================================================================
   *  RENDER
   * ================================================================ */
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">

      {/* ================= Header ================= */}
      <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:border-teal-400/15 bg-white/85 dark:bg-slate-900/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] print:hidden">
        <Header />
      </nav>

      {/* ================= Body ================= */}
      <div className="flex-1 min-h-0 relative">

        {/* Mobile backdrop */}
        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30 bg-slate-900/50 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-300"
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed top-[70px] bottom-0 rtl:right-0 ltr:left-0 w-64 z-40
            transform transition-transform duration-300 ease-out
            ${isSidebarOpen ? "translate-x-0" : "rtl:translate-x-full ltr:-translate-x-full"}
            lg:!translate-x-0
            rtl:border-l ltr:border-r border-teal-500/10 dark:border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:from-slate-900/95 dark:via-slate-900/98 dark:to-slate-900/95
            backdrop-blur-xl shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden overflow-hidden
          `}
        >
          <UserSidebar />
        </aside>

        {/* Main */}
        <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">
            <div className="px-3 sm:px-4 md:px-6 py-4 sm:py-6">

              {/* ============ Page Header ============ */}
              <div className="max-w-4xl mx-auto mb-5">
                <div className="flex items-center gap-3">
                  <span className="shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40">
                    <Upload size={20} className="text-amber-500" />
                  </span>
                  <div className="min-w-0">
                    <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-l from-teal-700 via-teal-600 to-amber-500 dark:from-amber-200 dark:via-teal-300 dark:to-teal-400 bg-clip-text text-transparent">
                      {t("fileUploadManager.pageTitle")}
                    </h1>
                    <p className="text-[11px] sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {t("fileUploadManager.pageSubtitle")}
                    </p>
                  </div>
                  <span className="ms-auto hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold text-amber-800 dark:text-amber-200 bg-amber-300/20 border border-amber-300/50">
                    <Sparkles size={11} />
                    {t("fileUploadManager.proBadge")}
                  </span>
                </div>
              </div>

              {/* ============ Upload Zone ============ */}
              <div className="max-w-4xl mx-auto mb-5">
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`
                    relative rounded-2xl
                    bg-white/70 dark:bg-slate-900/70
                    backdrop-blur-xl
                    border-2 border-dashed
                    shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)]
                    dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
                    overflow-hidden
                    transition-all duration-300
                    p-4 sm:p-6 lg:p-8
                    ${isDragging
                      ? "border-teal-500 bg-teal-50/60 dark:bg-teal-500/5 scale-[1.01] shadow-[0_15px_50px_-15px_rgba(20,184,166,0.6)]"
                      : "border-teal-500/40 dark:border-teal-400/30 hover:border-teal-500/70 hover:bg-teal-50/30 dark:hover:bg-teal-500/5"
                    }
                  `}
                >
                  {/* تزئینات */}
                  <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
                  </div>

                  <div className="relative flex flex-col items-center text-center gap-3">
                    <span
                      className={`
                        flex h-14 w-14 items-center justify-center rounded-2xl
                        bg-gradient-to-br from-amber-300/30 to-teal-400/20
                        border border-amber-300/40
                        transition-transform duration-300
                        ${isDragging ? "scale-110 rotate-3" : ""}
                      `}
                    >
                      <CloudUpload size={24} className="text-amber-500" />
                    </span>

                    <div>
                      <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-amber-100">
                        {isDragging
                          ? t("fileUploadManager.dropZone.dropHere")
                          : t("fileUploadManager.dropZone.selectOrDrag")}
                      </p>
                      <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {t("fileUploadManager.dropZone.allowedFormats")}
                      </p>
                    </div>

                    <input
                      id="file-upload"
                      ref={fileInputRef}
                      type="file"
                      accept={ALLOWED_EXT.join(",")}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="
                        group relative overflow-hidden
                        flex items-center gap-2
                        px-5 py-2.5 rounded-xl
                        text-xs sm:text-sm font-bold text-white
                        bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
                        shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)]
                        hover:from-teal-600 hover:via-teal-500 hover:to-teal-400
                        hover:shadow-[0_12px_32px_-8px_rgba(20,184,166,1)]
                        hover:scale-[1.02] active:scale-95
                        transition-all duration-300
                      "
                    >
                      <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                      <Upload size={15} className="relative" />
                      <span className="relative">{t("fileUploadManager.dropZone.selectButton")}</span>
                    </button>
                  </div>
                </div>

                {/* ============ Pending Item Preview ============ */}
                {pending && (
                  <div
                    className={`
                      mt-4 rounded-2xl overflow-hidden
                      bg-white/70 dark:bg-slate-900/70
                      backdrop-blur-xl
                      border
                      shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)]
                      dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
                      transition-all duration-300
                      ${pending.error
                        ? "border-rose-400/50"
                        : "border-teal-500/30 dark:border-teal-400/20"
                      }
                    `}
                  >
                    <div className={`h-[2px] ${pending.error ? "bg-gradient-to-l from-transparent via-rose-400/80 to-transparent" : "bg-gradient-to-l from-transparent via-amber-400/70 to-transparent"}`} />

                    <div className="p-4 sm:p-5">
                      {pending.error ? (
                        <div className="flex items-start gap-3">
                          <span className="shrink-0 h-9 w-9 flex items-center justify-center rounded-xl bg-rose-500/15 border border-rose-400/40 text-rose-500">
                            <AlertTriangle size={16} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-rose-700 dark:text-rose-300">
                              {t("fileUploadManager.fileError")}
                            </p>
                            <p className="text-xs text-rose-600 dark:text-rose-300 mt-1 break-words">
                              {pending.error}
                            </p>
                          </div>
                          <button
                            onClick={cancelAdd}
                            className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                          >
                            <X size={15} />
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* File Info */}
                          <div className="flex items-center gap-3">
                            <span className="shrink-0 h-11 w-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40 text-amber-700 dark:text-amber-200">
                              <FileSpreadsheet size={18} />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm sm:text-base font-bold text-slate-800 dark:text-amber-100 break-all">
                                {pending.name}
                              </p>
                              {formatSize(pending.size) && (
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                  {formatSize(pending.size)}
                                </p>
                              )}
                            </div>
                            {pending.loading && <Spinner tone="teal" size={20} />}
                          </div>

                          {/* Body */}
                          {pending.loading ? (
                            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                              <Loader2 size={14} className="animate-spin text-teal-500" />
                              {t("fileUploadManager.checkingFile")}
                            </div>
                          ) : pending.computable ? (
                            <>
                              <div className="flex flex-wrap gap-2 mt-3">
                                <Chip icon={Rows3} label={`${t("fileUploadManager.rows")} ${pending.rows}`} tone="blue" />
                                {pending.cols !== null && (
                                  <Chip icon={Columns3} label={`${t("fileUploadManager.cols")} ${pending.cols}`} tone="purple" />
                                )}
                                {pending.estimatedTokens != null && (
                                  <Chip icon={Coins} label={`${t("fileUploadManager.estimatedTokens")} ${pending.estimatedTokens}`} tone="emerald" />
                                )}
                              </div>

                              {pending.sufficientTokens === false && (
                                <div className="mt-3 rounded-xl bg-rose-500/10 border border-rose-400/40 p-3 flex items-start gap-2.5">
                                  <AlertTriangle size={15} className="text-rose-500 shrink-0 mt-0.5" />
                                  <p className="text-xs text-rose-700 dark:text-rose-300 font-medium leading-6">
                                    {pending.estimatedTokens
                                      ? t("fileUploadManager.insufficientTokensDetailed", { need: pending.estimatedTokens })
                                      : t("fileUploadManager.insufficientTokens")}
                                  </p>
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                              {t("fileUploadManager.cannotCalculate")}
                            </p>
                          )}

                          {/* Actions */}
                          {!pending.loading && (
                            <div className="mt-4 pt-4 border-t border-teal-500/10 dark:border-teal-400/10">
                              <p className="text-xs font-bold text-slate-600 dark:text-slate-300 mb-3">
                                {t("fileUploadManager.confirmQuestion")}
                              </p>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={confirmAdd}
                                  disabled={pending.loading || submitting || pending.sufficientTokens === false}
                                  className="
                                    group relative overflow-hidden
                                    flex-1 sm:flex-initial
                                    flex items-center justify-center gap-2
                                    px-5 py-2.5 rounded-xl
                                    text-xs sm:text-sm font-bold text-white
                                    bg-gradient-to-l from-teal-700 via-teal-600 to-teal-500
                                    shadow-[0_8px_24px_-8px_rgba(15,118,110,0.8)]
                                    hover:from-teal-600 hover:via-teal-500 hover:to-teal-400
                                    hover:scale-[1.02] active:scale-95
                                    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                                    transition-all duration-300
                                  "
                                >
                                  <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                                  {submitting ? <Spinner tone="white" size={14} /> : <Check size={14} className="relative" />}
                                  <span className="relative">
                                    {submitting
                                      ? t("fileUploadManager.submitting")
                                      : t("fileUploadManager.confirm")}
                                  </span>
                                </button>

                                <button
                                  onClick={cancelAdd}
                                  disabled={submitting}
                                  className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-200 bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 disabled:opacity-50 transition-all duration-200"
                                >
                                  <X size={14} />
                                  {t("fileUploadManager.cancel")}
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ============ Pending Files List ============ */}
              <div className="max-w-4xl mx-auto mb-5">
                <div className="relative rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-amber-400/30 dark:border-amber-400/20 shadow-[0_10px_40px_-20px_rgba(251,191,36,0.4)] dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)] overflow-hidden">
                  <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />
                    <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/15 blur-3xl dark:bg-amber-400/10" />
                  </div>

                  <div className="relative p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-amber-500/20 border border-amber-300/50">
                          <Clock size={14} className="text-amber-500" />
                        </span>
                        <h2 className="text-sm sm:text-base font-bold text-slate-800 dark:text-amber-100">
                          {t("fileUploadManager.pendingSection.title")}
                        </h2>
                        <span className="text-[11px] font-bold text-amber-800 dark:text-amber-200 bg-amber-400/20 px-2 py-0.5 rounded-full tabular-nums border border-amber-400/40">
                          {pendingFiles.length}
                        </span>
                      </div>
                    </div>

                    {listLoading ? (
                      <div className="py-10 flex justify-center">
                        <Spinner tone="amber" size={24} />
                      </div>
                    ) : pendingFiles.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="h-12 w-12 mx-auto mb-3 rounded-full border-2 border-dashed border-amber-400/40 flex items-center justify-center">
                          <Clock size={18} className="animate-pulse text-amber-400/70" />
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {t("fileUploadManager.pendingSection.empty")}
                        </p>
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {pendingFiles.map((item) => (
                          <li
                            key={item.id}
                            className="group flex flex-wrap items-center gap-3 p-3 rounded-xl bg-amber-400/5 dark:bg-amber-400/5 border border-amber-400/25 dark:border-amber-400/20 hover:border-amber-400/60 transition-all duration-200"
                          >
                            <span className="shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-amber-500/20 border border-amber-300/50 text-amber-700 dark:text-amber-200">
                              <FileText size={16} />
                            </span>

                            <div className="min-w-0 flex-1">
                              <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 break-all">
                                {item.name}
                              </p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                                {formatSize(item.size) && (
                                  <span className="text-[10px] text-slate-500 dark:text-slate-400">
                                    {formatSize(item.size)}
                                  </span>
                                )}
                                {(item.updatedAt || item.createdAt) && (
                                  <span className="text-[10px] text-slate-500 dark:text-slate-400 tabular-nums">
                                    {moment(item.updatedAt || item.createdAt)
                                      .locale("fa")
                                      .format("jYYYY/jMM/jDD HH:mm")}
                                  </span>
                                )}
                              </div>

                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {item.rows !== null && (
                                  <Chip icon={Rows3} label={`${t("fileUploadManager.rows")} ${item.rows}`} tone="blue" size="xs" />
                                )}
                                {item.cols !== null && (
                                  <Chip icon={Columns3} label={`${t("fileUploadManager.cols")} ${item.cols}`} tone="purple" size="xs" />
                                )}
                                {item.estimatedTokens !== null && (
                                  <Chip icon={Coins} label={`${t("fileUploadManager.token")} ${item.estimatedTokens}`} tone="emerald" size="xs" />
                                )}
                              </div>

                              {item.sufficientTokens === false && (
                                <p className="mt-1.5 text-[10px] font-bold text-rose-500">
                                  {t("fileUploadManager.notEnoughTokensShort")}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-1.5 shrink-0">
                              <button
                                type="button"
                                onClick={() => confirmPendingFile(item)}
                                disabled={pendingConfirmingId === item.id || pendingDeletingId === item.id || item.sufficientTokens === false}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-l from-teal-700 to-teal-500 shadow-[0_4px_16px_-6px_rgba(15,118,110,0.7)] hover:scale-[1.03] active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-200"
                              >
                                {pendingConfirmingId === item.id ? (
                                  <Spinner tone="white" size={12} />
                                ) : (
                                  <Check size={12} />
                                )}
                                {pendingConfirmingId === item.id ? "..." : t("fileUploadManager.confirm")}
                              </button>

                              <button
                                type="button"
                                onClick={() => deletePendingFile(item)}
                                disabled={pendingDeletingId === item.id || pendingConfirmingId === item.id}
                                className="h-8 w-8 flex items-center justify-center rounded-lg text-rose-600 dark:text-rose-300 bg-rose-500/10 border border-rose-400/40 hover:bg-rose-500/20 hover:scale-105 active:scale-95 transition-all duration-200 disabled:opacity-40"
                                title={t("fileUploadManager.delete")}
                              >
                                {pendingDeletingId === item.id ? (
                                  <Spinner tone="rose" size={12} />
                                ) : (
                                  <Trash2 size={13} />
                                )}
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* ============ Confirmed Files List ============ */}
              <div className="max-w-4xl mx-auto">
                <div className="relative rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-teal-500/20 dark:border-teal-400/15 shadow-[0_10px_40px_-20px_rgba(15,118,110,0.45)] dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)] overflow-hidden">
                  <div className="pointer-events-none absolute inset-0 rounded-2xl overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
                    <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/10 blur-3xl dark:bg-amber-400/5" />
                    <div className="absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/10 blur-3xl dark:bg-teal-500/5" />
                  </div>

                  <div className="relative p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-300/30 to-teal-400/20 border border-emerald-400/40">
                          <CheckCircle2 size={14} className="text-emerald-500" />
                        </span>
                        <h2 className="text-sm sm:text-base font-bold text-slate-800 dark:!text-amber-100">
                          {t("fileUploadManager.confirmedSection.title")}
                        </h2>
                        <span className="text-[11px] font-bold text-teal-700 dark:text-teal-300 bg-teal-500/15 px-2 py-0.5 rounded-full tabular-nums border border-teal-500/30">
                          {confirmedFiles.length}
                        </span>
                      </div>
                    </div>

                    {listLoading ? (
                      <div className="py-10 flex justify-center">
                        <Spinner tone="teal" size={24} />
                      </div>
                    ) : confirmedFiles.length === 0 ? (
                      <div className="text-center py-10">
                        <div className="h-12 w-12 mx-auto mb-3 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
                          <FileSpreadsheet size={18} className="animate-pulse text-teal-400/70" />
                        </div>
                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                          {t("fileUploadManager.confirmedSection.empty")}
                        </p>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                          {t("fileUploadManager.confirmedSection.emptyHint")}
                        </p>
                      </div>
                    ) : (
                      <>
                        <ul className="space-y-2">
                          {paginated.map((item) => (
                            <li
                              key={item.id}
                              className="group flex flex-wrap items-center gap-3 p-3 rounded-xl bg-teal-400/5 dark:bg-teal-400/5 border border-teal-400/20 dark:border-teal-400/15 hover:border-teal-400/50 hover:bg-teal-400/10 transition-all duration-200"
                            >
                              <span className="shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40 text-amber-700 dark:text-amber-200">
                                <FileSpreadsheet size={16} />
                              </span>

                              <div className="min-w-0 flex-1">
                                <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 break-all">
                                  {item.name}
                                </p>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                                  {formatSize(item.size) && (
                                    <span className="text-[10px] text-slate-500 dark:text-slate-400">
                                      {formatSize(item.size)}
                                    </span>
                                  )}
                                  {(item.updatedAt || item.createdAt) && (
                                    <span className="text-[10px] text-slate-500 dark:text-slate-400 tabular-nums">
                                      {moment(item.updatedAt || item.createdAt)
                                        .locale("fa")
                                        .format("jYYYY/jMM/jDD HH:mm")}
                                    </span>
                                  )}
                                </div>

                                {item.computable && (
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    <Chip icon={Rows3} label={`${t("fileUploadManager.rows")} ${item.rows}`} tone="blue" size="xs" />
                                    {item.cols !== null && (
                                      <Chip icon={Columns3} label={`${t("fileUploadManager.cols")} ${item.cols}`} tone="purple" size="xs" />
                                    )}
                                  </div>
                                )}
                              </div>

                              <button
                                type="button"
                                onClick={() => requestDelete(item)}
                                disabled={deletingId === item.id}
                                className="h-8 w-8 flex items-center justify-center rounded-lg text-rose-600 dark:text-rose-300 bg-rose-500/10 border border-rose-400/40 hover:bg-rose-500/20 hover:scale-105 active:scale-95 transition-all duration-200 disabled:opacity-40 shrink-0"
                                title={t("fileUploadManager.delete")}
                              >
                                {deletingId === item.id ? (
                                  <Spinner tone="rose" size={12} />
                                ) : (
                                  <Trash2 size={13} />
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>

                        {totalPages > 1 && (
                          <div className="flex justify-center items-center gap-1.5 mt-5 pt-4 border-t border-teal-500/10 dark:border-teal-400/10 flex-wrap">
                            <button
                              type="button"
                              onClick={() => goToPage(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="h-9 w-9 flex items-center justify-center rounded-lg text-teal-700 dark:text-amber-200 bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:hover:scale-100"
                            >
                              <ChevronRight size={14} />
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                              <button
                                key={page}
                                type="button"
                                onClick={() => goToPage(page)}
                                className={`
                                  min-w-[2.25rem] h-9 px-3 rounded-lg text-xs font-bold tabular-nums transition-all duration-200
                                  ${page === currentPage
                                    ? "bg-gradient-to-l from-teal-700 to-teal-500 text-white shadow-[0_6px_20px_-6px_rgba(15,118,110,0.8)]"
                                    : "bg-white/60 dark:bg-white/5 text-teal-700 dark:text-amber-200 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10"
                                  }
                                `}
                              >
                                {page}
                              </button>
                            ))}

                            <button
                              type="button"
                              onClick={() => goToPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              className="h-9 w-9 flex items-center justify-center rounded-lg text-teal-700 dark:text-amber-200 bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20 hover:border-amber-300/60 hover:bg-amber-300/10 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 active:scale-95 disabled:hover:scale-100"
                            >
                              <ChevronLeft size={14} />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </main>
      </div>

      {/* ================= Delete Confirm ================= */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center pt-24 px-4 bg-slate-900/40 dark:!bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl overflow-hidden bg-white dark:!bg-slate-900 border border-rose-400/40 shadow-[0_25px_60px_-20px_rgba(244,63,94,0.6)]">
            <div className="h-[2px] bg-gradient-to-l from-transparent via-rose-400/80 to-transparent" />
            <div className="p-4 flex flex-col gap-3">
              <div className="flex items-start gap-2.5">
                <span className="shrink-0 h-9 w-9 flex items-center justify-center rounded-xl bg-rose-500/15 border border-rose-400/40 text-rose-500">
                  <AlertTriangle size={16} />
                </span>
                <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-rose-100 leading-6">
                  {t("fileUploadManager.deleteConfirm", { name: deleteTarget.name })}
                </p>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deletingId === deleteTarget.id}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold text-white bg-gradient-to-l from-rose-700 to-rose-500 shadow-[0_6px_20px_-6px_rgba(244,63,94,0.8)] hover:scale-[1.02] active:scale-95 transition-all duration-300 disabled:opacity-60"
                >
                  {deletingId === deleteTarget.id ? <Spinner tone="white" size={14} /> : <Trash2 size={14} />}
                  {deletingId === deleteTarget.id
                    ? t("fileUploadManager.deleting")
                    : t("fileUploadManager.delete")}
                </button>
                <button
                  type="button"
                  onClick={cancelDelete}
                  disabled={deletingId === deleteTarget.id}
                  className="flex-1 py-2.5 px-4 rounded-xl text-xs font-bold text-slate-700 dark:!text-slate-200 bg-slate-100 dark:!bg-slate-800 border border-slate-300/60 dark:border-slate-700 hover:bg-slate-200 dark:hover:!bg-slate-700 transition-all duration-200 disabled:opacity-60"
                >
                  {t("fileUploadManager.cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
          scroll-behavior: smooth;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 999px; margin: 6px 0; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px; border: 2px solid transparent; background-clip: padding-box;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(45,212,191,0.7), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(45,212,191,1), rgba(251,191,36,0.9));
          box-shadow: 0 0 12px -2px rgba(20,184,166,0.7);
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(94,234,212,0.65), rgba(251,191,36,0.65));
        }
      `}</style>
    </div>
  );
}

/* ================================================================
 *  Chip helper
 * ================================================================ */
const Chip = ({ icon: Icon, label, tone = "teal", size = "sm" }) => {
  const tones = {
    blue: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
    purple: "bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30",
    emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    teal: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30",
    amber: "bg-amber-500/15 text-amber-800 dark:text-amber-200 border-amber-500/30",
  };
  const sizes = {
    xs: "text-[10px] px-1.5 py-0.5 gap-1",
    sm: "text-[11px] px-2 py-0.5 gap-1.5",
  };
  return (
    <span
      className={`
        inline-flex items-center font-bold rounded-lg border backdrop-blur-sm
        ${tones[tone]} ${sizes[size]}
      `}
    >
      {Icon && <Icon size={size === "xs" ? 9 : 11} />}
      <span>{label}</span>
    </span>
  );
};

const FileUploadManager = () => (
  <SidebarProvider>
    <FileUploadManagerInner />
  </SidebarProvider>
);

export default FileUploadManager;